import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Input, Select, Textarea } from '../../components/ui/FormField.js';
import { Modal } from '../../components/ui/Modal.js';
import {
  type BulkCodeResponse,
  type ShareCandidate,
  discountsApi,
} from '../../services/discounts.js';
import { CodeBulkResultDialog } from './CodeBulkResultDialog.js';

const REASON_MAX = 300;

function candidateName(u: ShareCandidate) {
  return [u.first_name, u.last_name].filter(Boolean).join(' ').trim() || u.email || u._id;
}

const pickupDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-MY', {
    timeZone: 'Asia/Kuala_Lumpur',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

interface ShareCodesDialogProps {
  open: boolean;
  discountId: string;
  codeIds: string[];
  /** id → code text, so the results name each code. */
  codeLabels: Record<string, string>;
  onClose: () => void;
  /** After the results are dismissed. */
  onDone: () => void;
}

/**
 * 391 (#1312, mock-up screen 2): share the selected codes with ONE customer (searched by name, email or phone), an
 * optional booking of theirs and a reason. Each share waits for another approver; the per-code results follow.
 */
export function ShareCodesDialog({
  open,
  discountId,
  codeIds,
  codeLabels,
  onClose,
  onDone,
}: ShareCodesDialogProps) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [customer, setCustomer] = useState<ShareCandidate | null>(null);
  const [bookingId, setBookingId] = useState('');
  const [bookingTerm, setBookingTerm] = useState('');
  const [bookingQ, setBookingQ] = useState('');
  const [reason, setReason] = useState('');
  const [result, setResult] = useState<BulkCodeResponse | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 300);
    return () => clearTimeout(t);
  }, [term]);
  useEffect(() => {
    const t = setTimeout(() => setBookingQ(bookingTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [bookingTerm]);

  const people = useQuery({
    queryKey: ['share-customer-search', debounced],
    queryFn: () => discountsApi.searchUsers(debounced),
    enabled: open && !customer && debounced.length >= 2,
  });
  const bookings = useQuery({
    queryKey: ['share-customer-bookings', customer?._id, bookingQ],
    queryFn: () => discountsApi.customerBookings(customer?._id as string, bookingQ || undefined),
    enabled: open && Boolean(customer),
  });

  const share = useMutation({
    mutationFn: () =>
      discountsApi.shareCodes(discountId, codeIds, {
        user_id: customer?._id as string,
        booking_id: bookingId || null,
        reason: reason.trim(),
      }),
    onSuccess: setResult,
  });

  // Only the chosen customer's bookings, whatever the list answered (the platform refuses any other on share).
  const bookingOptions = (bookings.data?.items ?? []).filter(
    (b) => String(b.user_id) === String(customer?._id),
  );

  function reset() {
    setBookingTerm('');
    setBookingQ('');
    setTerm('');
    setDebounced('');
    setCustomer(null);
    setBookingId('');
    setReason('');
    share.reset();
  }

  const count = codeIds.length;
  const canShare = Boolean(customer) && reason.trim().length > 0 && reason.length <= REASON_MAX;

  if (result) {
    return (
      <CodeBulkResultDialog
        result={result}
        labels={codeLabels}
        onClose={() => {
          setResult(null);
          reset();
          onDone();
        }}
      />
    );
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={`Share ${count} code${count === 1 ? '' : 's'}`}
    >
      <div className="space-y-4 px-6 py-4 text-sm text-ink">
        <p className="text-steel">
          The customer can use {count === 1 ? 'it' : 'them'} only after another staff member
          approves.
        </p>

        <div className="flex flex-col gap-1">
          <label htmlFor="share-customer" className="font-medium">
            Customer
          </label>
          {customer ? (
            <div className="flex items-center justify-between rounded-md border border-hairline px-3 py-2">
              <span>
                <span className="font-medium">{candidateName(customer)}</span>
                {customer.email && <span className="ml-2 text-steel">{customer.email}</span>}
              </span>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setCustomer(null);
                  setBookingId('');
                }}
              >
                Change
              </Button>
            </div>
          ) : (
            <>
              <Input
                id="share-customer"
                placeholder="Search by name, email or phone…"
                value={term}
                onInput={(e) => setTerm((e.target as HTMLInputElement).value)}
              />
              {(people.data?.items ?? []).length > 0 && (
                <ul className="max-h-48 divide-y divide-hairline overflow-y-auto rounded-md border border-hairline bg-canvas">
                  {(people.data?.items ?? []).map((u) => (
                    <li key={u._id}>
                      <button
                        type="button"
                        className="block w-full px-3 py-2 text-left hover:bg-surface"
                        onClick={() => setCustomer(u)}
                      >
                        <span className="font-medium">{candidateName(u)}</span>
                        <span className="ml-2 text-steel">
                          {[u.email, u.phone].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-steel">Type at least 2 characters.</p>
            </>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="share-booking" className="font-medium">
            Related booking <span className="font-normal text-steel">(optional)</span>
          </label>
          <Input
            aria-label="Booking number"
            placeholder="Booking number…"
            value={bookingTerm}
            disabled={!customer}
            onInput={(e) => setBookingTerm((e.target as HTMLInputElement).value)}
          />
          <Select
            id="share-booking"
            value={bookingId}
            disabled={!customer}
            onChange={(e) => setBookingId(e.target.value)}
          >
            <option value="">No booking</option>
            {bookingOptions.map((b) => (
              <option key={b.id} value={b.id}>
                {`${b.id.slice(-8)} · ${pickupDay(b.pickup_at)} · ${b.status}`}
              </option>
            ))}
          </Select>
          {!customer && <p className="text-xs text-steel">Choose the customer first.</p>}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="share-reason" className="font-medium">
            Reason
          </label>
          <Textarea
            id="share-reason"
            rows={3}
            maxLength={REASON_MAX}
            placeholder="Why is this customer getting the code?"
            value={reason}
            onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
          />
          <p className="text-right text-xs text-steel">
            {reason.length} / {REASON_MAX}
          </p>
        </div>

        {share.isError && (
          <p role="alert" className="text-red-600">
            {(share.error as { message?: string }).message ?? 'Could not share the codes.'}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={!canShare}
            loading={share.isPending}
            onClick={() => share.mutate()}
          >
            {`Share ${count} code${count === 1 ? '' : 's'}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
